import { useState } from 'react'
import backgroundImage from '../assets/background.jfif'
import logo from '../assets/minister.png'
import { loginUser, requestPasswordReset } from '../services/auth.services'

interface LoginPageProps {
  onLoginSuccess: () => void
  onRequireOtp: (userId: string) => void
}

export function LoginPage({ onLoginSuccess, onRequireOtp }: LoginPageProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  
  // Toggle between Login and Forgot Password views
  const [isForgotPassword, setIsForgotPassword] = useState(false)

  // Standard Login Handler
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')
    setIsLoading(true)

    try {
      const response = await loginUser({ 
        email: email.trim(), 
        password 
      })

      if (response?.requires2FA) {
        onRequireOtp(response.userId)
        return
      }

      if (response?.access_token) {
        localStorage.setItem('access_token', response.access_token)
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user))
        }
        onLoginSuccess()
      } else {
        setError('စနစ်အတွင်းသို့ ဝင်ရောက်ရာတွင် အဆင်မပြေပါ။')
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('စနစ်အတွင်းသို့ ဝင်ရောက်ရာတွင် အဆင်မပြေပါ၊ ခဏအကြာတွင် ပြန်လည်ကြိုးစားပါ။')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Forgot Password Request Handler
  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccessMessage('')
    setIsLoading(true)

    try {
      await requestPasswordReset(email.trim())
      setSuccessMessage('လျှို့ဝှက်နံပါတ် ပြန်လည်သတ်မှတ်ရန် လင့်ခ် (သို့မဟုတ် မူရင်းသင်္ကေတ) ကို သင့်အီးမေးလ်သို့ ပို့ပေးထားပါသည်။')
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('အီးမေးလ် ပေးပို့ရာတွင် အဆင်မပြေပါ၊ ခဏအကြာတွင် ပြန်လည်ကြိုးစားပါ။')
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Switch view helper to clear temporary messages
  const toggleView = (showForgotPassword: boolean) => {
    setIsForgotPassword(showForgotPassword)
    setError('')
    setSuccessMessage('')
  }

  return (
    <div
      style={{ backgroundImage: `url(${backgroundImage})` }}
      className="relative flex min-h-screen flex-col lg:flex-row items-center justify-center bg-cover bg-center bg-no-repeat p-6 gap-8 lg:gap-16 lg:px-24 font-sans"
    >
      <div className="absolute inset-0 bg-slate-950/50 pointer-events-none" />

      {/* Left Branding Area */}
      <div className="z-10 w-full max-w-xl text-center lg:text-left space-y-4 lg:pr-8">
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-normal tracking-wide leading-normal text-slate-300">
          ပြည်ထောင်စုသမ္မတ မြန်မာနိုင်ငံတော်အစိုးရ
        </h1>
        <span className="text-base font-semibold tracking-wider text-blue-400 bg-blue-500/10 px-3 py-1 rounded-md backdrop-blur-md">
          ဥပဒေရေးရာဝန်ကြီးဌာန
        </span>

        <div className="h-[1px] w-16 bg-slate-700 mx-auto lg:mx-0 mt-4" />
      </div>

      {/* Login / Forgot Password Card */}
      <div className="z-10 w-full max-w-md space-y-6 rounded-2xl border border-white/10 bg-white/95 p-8 shadow-2xl backdrop-blur-sm transition-all duration-300">
        <div className="text-center">
          <div className="mx-auto flex h-28 w-28 items-center justify-center overflow-hidden bg-transparent">
            <img src={logo} alt="တံဆိပ်တုံး" className="h-full w-full object-contain" />
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
            {isForgotPassword ? 'လျှို့ဝှက်နံပါတ် မေ့နေပါသလား' : 'စနစ်အတွင်းသို့ဝင်ရန်'}
          </h2>
          {isForgotPassword && (
            <p className="mt-1 text-sm text-slate-600">
              သင့်အကောင့်တွင် အသုံးပြုထားသော အီးမေးလ် ထည့်သွင်းပေးပါ။
            </p>
          )}
        </div>

        {isForgotPassword ? (
          /* FORGOT PASSWORD FORM */
          <form className="space-y-4" onSubmit={handleForgotPasswordSubmit}>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700">
                အီးမေးလ် လိပ်စာ
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                placeholder="အီးမေးလ်ထည့်ပါ"
              />
            </div>

            {error && <p className="text-sm font-medium text-red-600">{error}</p>}
            {successMessage && <p className="text-sm font-medium text-emerald-600">{successMessage}</p>}

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  ဆောင်ရွက်နေပါသည်...
                </span>
              ) : (
                'ပို့ဆောင်မည်'
              )}
            </button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => toggleView(false)}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
              >
                ← စနစ်အတွင်းသို့ ဝင်ရောက်ရန် စာမျက်နှာသို့ ပြန်သွားမည်
              </button>
            </div>
          </form>
        ) : (
          /* LOGIN FORM */
          <form className="space-y-4" onSubmit={handleLoginSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                အီးမေးလ် လိပ်စာ
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                placeholder="အီးမေးလ်ထည့်ပါ"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                လျှို့ဝှက်နံပါတ်
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                placeholder="လျှို့ဝှက်နံပါတ်ထည့်ပါ"
              />
            </div>

            {error && <p className="text-sm font-medium text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={isLoading}
              className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-blue-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  ဆောင်ရွက်နေပါသည်...
                </span>
              ) : (
                'အကောင့်ဝင်ရန်'
              )}
            </button>

            {/* Forgot Password Link under the Submit Button (Right-aligned) */}
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={() => toggleView(true)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-500 focus:outline-none"
              >
                လျှို့ဝှက်နံပါတ် မေ့နေပါသလား?
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}